import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { icons } from '../Donee/components/common/Icon/constants';
import { colors } from '../theme';
import { Text } from '../Donee/components/common/Text/Text';
import { isAppleDevice } from '../Donee/components/common/utils';
import { CrossIcon } from '../icons';
import { appLinks, assetUrl } from '../../utils/webConfig';
import { Donee, useDonorProfile, useDonee } from '../../api';
import { usePushEvents } from '../../hooks/PushEvents';
import {
    appDownloadEvent,
    clickEvent,
    successEvent,
    viewEvent,
} from '../../hooks/PushEvents/clevertapEvents';
import { copyDoneeIdToClipboard } from '../../utils/clipboard';
import DeepLinkCopiedModal from '../Donee/components/CheckoutWizard/ThankYouWizard/DeepLinkCopiedModal';
import { getDoneeAmountDeepLink, useDeepLink } from '../../hooks/useDeepLink';
import { FeatureFlag } from '../../constants';
import { Button } from '../ui';

const postEventPayload = {
    name: 'footer banner',
    location: 'giftmountsbanner',
    action: 'completed',
    voucher: 'DDL voucher',
};

const eventPayload = {
    location: 'giftmountsbanner',
    action: 'clicked',
};

const deepLinkTitle = (donee: Donee | undefined) => (
    <>
        We’ve <span className="deep-link-copied-text">copied</span> a link
        {donee && (
            <>
                <br />
                to {donee.organization.name}
            </>
        )}
    </>
);

const StyledDiv = styled.div`
    padding: 8px 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    position: fixed;
    left: 0;
    bottom: 0vh;
    z-index: 999;
    border-top: 1px solid ${colors.utilitySeparatorColor};
    background: rgba(250, 250, 252, 0.8);
    backdrop-filter: blur(10px);
    .givelify-icon {
        width: 54px;
        height: 54px;
        border-radius: 12px;
    }
    .banner-close-icon {
        cursor: pointer;
        width: 40px;
        height: 40px;
        fill: ${colors.primarySecondaryContent};
    }
`;

let isClosed = false;
const hideBanner = window.innerWidth > 500;
const link = appLinks.giftAmountBanner;
const handleDownload = () => {
    window.location.href = isAppleDevice() ? link.apple : link.android;
};

export const getFooterBannerVisibility = (
    totalGiftsCount: number | undefined = 0,
    isFeatureEnabled: (featureKey: FeatureFlag) => boolean,
    isLoggedIn: boolean,
) => {
    if (!isAppleDevice()) return false;
    const isLoggedOutEnabled = isFeatureEnabled(
        FeatureFlag.OGA_APPGIFTAMOUNTBANNER_SIGNEDOUT_V2,
    );
    if (!isLoggedIn && isLoggedOutEnabled) {
        return true;
    }

    return isLoggedIn && totalGiftsCount > 0;
};

const FooterBanner: React.FC<{
    bannerControlValue: 'Variation' | 'Control';
}> = ({ bannerControlValue }) => {
    const [url, setUrl] = useState<string | undefined>(undefined);
    const { donee } = useDonee();
    const { pushEvent } = usePushEvents();
    const { donorProfile } = useDonorProfile();
    const [show, setShow] = useState<boolean>(true);

    useEffect(() => {
        if (!show || hideBanner || isClosed) return;
        const isLoggedIn = Boolean(donorProfile);
        pushEvent(appDownloadEvent, {
            location: 'giftmountsbanner',
            action: 'shown',
            isLoggedIn,
            'oga-appgiftamountbanner-signedout-v2': bannerControlValue,
        });
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [donorProfile, pushEvent]);

    const handleClose = useCallback(() => {
        setShow(false);
        isClosed = true;
        pushEvent(appDownloadEvent, {
            location: 'giftmountsbanner',
            action: 'dismissed',
            'oga-appgiftamountbanner-signedout-v2': bannerControlValue,
        });
    }, [setShow, pushEvent, bannerControlValue]);

    const handleDownloadClicked = useCallback(() => {
        pushEvent(appDownloadEvent, eventPayload);
        copyDoneeIdToClipboard(donee?.organization.doneeId);
        handleDownload();
    }, [donee, pushEvent]);

    const {
        deepLinkState,
        showCopiedModal,
        getDeepLink,
        isDeferredDeepLinkOn,
        setShowCopiedModal,
    } = useDeepLink(undefined, eventPayload, handleDownload);

    const handleDownloadApp = useCallback(() => {
        pushEvent(appDownloadEvent, eventPayload);
        getDeepLink(
            donee,
            donorProfile,
            donorProfile ? '/dl/giftAmount' : getDoneeAmountDeepLink(donee),
            undefined,
        ).then((s) => {
            s && setUrl(s);
        });
    }, [donee, donorProfile, getDeepLink, pushEvent]);

    if (!show || hideBanner || isClosed) return null;

    return (
        <>
            <StyledDiv>
                <img
                    src={assetUrl(icons.givelifyIcon)}
                    alt="Givelify Icon"
                    title="Givelify Icon"
                    className="givelify-icon"
                />
                <div className="banner-text-wrapper">
                    <Text
                        variant="small"
                        weight="extraBold"
                    >
                        Safe. Secure. Free!
                    </Text>
                    <Text variant="small">
                        Keep giving cheerfully with the Givelify app.
                    </Text>
                </div>
                <Button
                    id="footer-banner-open"
                    loading={deepLinkState.loading}
                    disabled={Boolean(deepLinkState.voucher)}
                    onClick={
                        isDeferredDeepLinkOn()
                            ? handleDownloadApp
                            : handleDownloadClicked
                    }
                    className="h-10 w-20 p-0 min-h-10 min-w-20"
                >
                    Open
                </Button>
                <CrossIcon
                    className="banner-close-icon"
                    onClick={handleClose}
                />
            </StyledDiv>
            <DeepLinkCopiedModal
                url={url}
                open={showCopiedModal}
                viewEvent={viewEvent}
                clickEvent={clickEvent}
                successEvent={successEvent}
                clickEventPayload={postEventPayload}
                title={deepLinkTitle(donee)}
                onClick={handleDownload}
                onClose={() => setShowCopiedModal(false)}
            />
        </>
    );
};

export default FooterBanner;
